import { useParams, useLocation } from 'react-router-dom'
import { useContext, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { APIContext } from 'context/APIContext'
import { ModalKey, ModalContext } from 'components/Modals/ModalContext'
import { default_equipment_profile } from 'functions/equipmentUtils'
import moment from 'moment'

import ScreenFrame from "components/Frame/ScreenFrame"
import KinesisProfileBreadCrumbs from 'components/Profile/KinesisProfileBreadCrumbs'
import ContentFrame from "components/Frame/ContentFrame"
import BrandColorButton from 'components/Buttons/BrandColorButton'
import EquipmentProfilePreviewCard from 'components/EquipmentProfile/EquipmentProfilePreviewCard'

export default function EquipmentView({ propProfileType = 'individual' }) {

    const { urlGroupId, urlAthleteId } = useParams()
    const location = useLocation()

    const { GetGroupEquipmentProfiles, GetIndividualEquipmentProfiles } = useContext(APIContext)
    const { CreateGroupEquipmentProfile, CreateIndividualEquipmentProfile, UpdateGroupEquipmentProfile, UpdateIndividualEquipmentProfile } = useContext(APIContext)
    const { ActivateGroupEquipmentProfile, ActivateIndividualEquipmentProfile, RegenerateActiveProgramIncompleteWorkouts } = useContext(APIContext)
    const { ShowModal } = useContext(ModalContext)

    const simulationClockDayOffset = useSelector((state) => state.simulationClock.simulationClockDayOffset)
    const clientDate = moment().local().add(simulationClockDayOffset, 'days').format('YYYY-MM-DD')

    const parsedUrlGroupId = urlGroupId != null ? parseInt(urlGroupId) : null
    const parsedUrlAthleteId = urlAthleteId != null ? parseInt(urlAthleteId) : null

    const [equipmentProfiles, setEquipmentProfiles] = useState(null)

    const LoadEquipmentProfiles = async () => {

        setEquipmentProfiles(null)

        var newEquipmentProfiles = null

        if (propProfileType === 'group') {
            newEquipmentProfiles = await GetGroupEquipmentProfiles(urlGroupId)
        }

        if (propProfileType === 'individual') {
            newEquipmentProfiles = await GetIndividualEquipmentProfiles(urlAthleteId)
        }

        setEquipmentProfiles(newEquipmentProfiles)

    }

    const SaveNewEquipmentProfile = async (equipmentProfileJSON, { name, description, activateOnSave }) => {

        var result = false

        if (propProfileType === 'group') {
            result = await CreateGroupEquipmentProfile(parsedUrlGroupId, name, description, activateOnSave ? 1 : 0, equipmentProfileJSON)
        }

        if (propProfileType === 'individual') {
            result = await CreateIndividualEquipmentProfile(parsedUrlAthleteId, name, description, activateOnSave ? 1 : 0, equipmentProfileJSON)
        }

        if (result === false) return false

        // Active equipment changed, so regenerate incomplete workouts with the new equipment

        if (activateOnSave) {
            await RegenerateActiveProgramIncompleteWorkouts(parsedUrlGroupId, parsedUrlAthleteId, clientDate)
        }

        await LoadEquipmentProfiles()

        return true
    }

    const SaveEquipmentProfileChanges = async (equipmentProfile, equipmentProfileJSON, { name, description, activateOnSave }) => {

        var result = false

        if (propProfileType === 'group') {
            result = await UpdateGroupEquipmentProfile(parsedUrlGroupId, equipmentProfile.id, name, description, equipmentProfileJSON)
        }

        if (propProfileType === 'individual') {
            result = await UpdateIndividualEquipmentProfile(parsedUrlAthleteId, equipmentProfile.id, name, description, equipmentProfileJSON)
        }

        if (result === false) return false

        // Update endpoints do not change active status, so activate separately when requested

        if (activateOnSave) {

            if (propProfileType === 'group') {
                result = await ActivateGroupEquipmentProfile(parsedUrlGroupId, equipmentProfile.id)
            }

            if (propProfileType === 'individual') {
                result = await ActivateIndividualEquipmentProfile(parsedUrlAthleteId, equipmentProfile.id)
            }

            if (result === false) return false
        }

        // Only the active equipment profile affects workouts

        if (equipmentProfile.active === 1 || activateOnSave) {
            await RegenerateActiveProgramIncompleteWorkouts(parsedUrlGroupId, parsedUrlAthleteId, clientDate)
        }

        await LoadEquipmentProfiles()

        return true
    }

    const OnCreateEquipmentProfileButton = () => {
        const modalData = {
            key: ModalKey.EditEquipmentProfile,
            size: 'third',
            titleText: 'Create Equipment Profile',
            displayNameAndDescription: true,
            displayActivateOnSaveToggle: true,
            equipmentProfileName: '',
            equipmentProfileDescription: '',
            equipmentProfileJSON: default_equipment_profile,
            buttonAction: SaveNewEquipmentProfile
        }

        ShowModal(modalData)
    }

    const OnEquipmentProfileSelected = (equipmentProfile) => {
        const modalData = {
            key: ModalKey.EditEquipmentProfile,
            size: 'third',
            titleText: 'Edit Equipment Profile',
            displayNameAndDescription: true,
            displayActivateOnSaveToggle: equipmentProfile.active !== 1,
            equipmentProfileName: equipmentProfile.name,
            equipmentProfileDescription: equipmentProfile.description ?? '',
            equipmentProfileJSON: JSON.parse(equipmentProfile.equipmentProfileJSON),
            buttonAction: (equipmentProfileJSON, nameAndDescription) => SaveEquipmentProfileChanges(equipmentProfile, equipmentProfileJSON, nameAndDescription)
        }

        ShowModal(modalData)
    }

    useEffect(() => { LoadEquipmentProfiles() }, [propProfileType, urlGroupId, urlAthleteId])

    const activeEquipmentProfile = equipmentProfiles?.find(profile => profile.active === 1)
    const inactiveEquipmentProfiles = equipmentProfiles?.filter(profile => profile.active === 0)

    if (equipmentProfiles === null) {
        return (
            <ScreenFrame>
                <div className='max-w-2xl mx-auto space-y-4'>
                    <div className='flex-shrink-0 h-20 bg-gray-200 rounded-lg animate-pulse' />
                    <div className='flex-shrink-0 h-[400px] bg-gray-200 rounded-lg animate-pulse' />
                </div>
            </ScreenFrame>
        )
    }

    return (
        <ScreenFrame>
            <div className='max-w-2xl mx-auto space-y-4'>
                <KinesisProfileBreadCrumbs 
                    propProfileType={propProfileType} 
                    profileName={location.state?.profileName} 
                    currentPageName='Equipment' 
                />

                <div className='flex items-center space-y-1 gap-x-2 pb-1'>
                    <h1 className="text-2xl font-semibold text-gray-900 tracking-tight flex-grow">
                        Equipment
                    </h1>

                    <div className="w-32">
                        <BrandColorButton
                            size='extra-small'
                            text="Create"
                            action={OnCreateEquipmentProfileButton}
                        />
                    </div>
                </div>

                {equipmentProfiles.length === 0 && (
                    <ContentFrame>
                        <div className='text-center text-gray-500 py-4'>No equipment profiles found</div>
                    </ContentFrame>
                )}

                {activeEquipmentProfile &&
                    <div>
                        <EquipmentProfilePreviewCard 
                            equipmentProfile={activeEquipmentProfile} 
                            OnEquipmentProfileSelected={OnEquipmentProfileSelected}
                        />
                    </div>
                }

                {inactiveEquipmentProfiles !== null && inactiveEquipmentProfiles.length > 0 && (
                    <>
                        <p>Other Profiles</p>

                        {inactiveEquipmentProfiles.map((equipmentProfile, index) => (
                            <div key={index}>
                                <EquipmentProfilePreviewCard
                                    equipmentProfile={equipmentProfile} 
                                    OnEquipmentProfileSelected={OnEquipmentProfileSelected}
                                />
                            </div>
                        ))}
                    </>
                )}
            </div>
        </ScreenFrame>
    )
}